import { useCustomers, useInvoicePreference, useMelioIntl, useSearchParams } from '@melio/ar-domain';
import { useToast } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { useState } from 'react';

import { CreateInvoiceExitModal } from './components';
import { CreateInvoiceScreen } from './screens';
import { CreateInvoiceSubmitTarget } from './types';
import { InvoiceFormValues, useCreateInvoiceActions, useInvoiceSummaryEnriched, usePrefetch } from './utils';

export type CreateInvoiceActivityProps = {
  onDone?: (id: string, shouldIssue?: boolean) => unknown;
  onError?: ErrorFunction;
  taxRateId?: string;
  discountId?: string;
  serviceChargeIds?: string[];
  onSetDiscount?: VoidFunction;
  onRemoveDiscount?: VoidFunction;
  onSetTaxRate?: VoidFunction;
  onAddServiceCharge?: VoidFunction;
  onRemoveServiceCharge?: (index: number) => void;
  onClose: VoidFunction;
};

export const CreateInvoiceActivity = forwardRef<CreateInvoiceActivityProps>(
  (
    {
      discountId,
      onClose,
      onRemoveDiscount,
      onSetDiscount,
      onSetTaxRate,
      onAddServiceCharge,
      onRemoveServiceCharge,
      onError: _onError,
      taxRateId,
      serviceChargeIds,
      onDone,
      ...props
    },
    ref
  ) => {
    const [error, setError] = useState<PlatformError>();
    const [submitType, setSubmitType] = useState<CreateInvoiceSubmitTarget>();
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isValid, setIsValid] = useState(false);

    const onError = (err: PlatformError) => [setError, _onError].forEach((fn) => fn?.(err)); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: customers, isLoading: isLoadingCustomers } = useCustomers();
    const [{ customerId }] = useSearchParams<{ customerId: string }>();

    // pre-fetch form data
    const { isLoading: isPreFetching } = usePrefetch({ onError });

    const { displayInvoiceNumber, isLoading: isLoadingInvoicePreferences } = useInvoicePreference();

    const { createInvoice, isSaving } = useCreateInvoiceActions({
      discountId,
      onError,
      taxRateId,
      serviceChargeIds,
    });

    const { formatMessage } = useMelioIntl();
    const toastFactory = useToast();

    const isLoading = [isPreFetching, isLoadingInvoicePreferences, isLoadingCustomers].some(Boolean);

    const [lineItems, setLineItems] = useState<InvoiceFormValues['lineItems']>([]);

    const { data: invoiceSummary, isFetching: isFetchingInvoiceSummary } = useInvoiceSummaryEnriched({
      params: {
        discountId,
        taxRateId,
        serviceChargesIds: serviceChargeIds,
        lineItems: lineItems
          .filter((item) => !!item.price && !!item.quantity)
          .map(({ price, quantity, taxable }) => ({ price, taxable, quantity })),
      },
      onError,
    });

    const handleInvoiceCreation = (
      data: InvoiceFormValues,
      target: CreateInvoiceSubmitTarget,
      onCreate?: (id: string) => void
    ) => {
      setSubmitType(target);
      createInvoice(data)
        .then(({ id }) => {
          onCreate?.(id);
        })
        .catch(onError)
        .finally(() => {
          setSubmitType(undefined);
        });
    };

    const onSubmit = (
      data: InvoiceFormValues,
      _event?: unknown,
      target: CreateInvoiceSubmitTarget = 'exitSaveAsDraft'
    ) => {
      switch (target) {
        case 'saveAndContinue':
          void handleInvoiceCreation(data, target, (id: string) => onDone?.(id, true));
          return;
        case 'exitSaveAsDraft':
        case 'saveAsDraft':
          void handleInvoiceCreation(data, target, (id) => {
            onDone?.(id, false);
            toastFactory({
              type: 'success',
              title: formatMessage('ar.invoiceLifecycle.activities.createInvoice.save.success.label'),
            });
          });
          return;
      }
    };

    const handleClose = () => {
      const isSubmitting = submitType;
      setIsExitModalOpen(isDirty && !isSubmitting);
      if (!isDirty) {
        onClose();
      }
    };

    return (
      <>
        <CreateInvoiceExitModal
          isOpen={isExitModalOpen}
          onCloseModal={() => !submitType && setIsExitModalOpen(false)}
          onSubmit={() => {
            document.querySelector<HTMLButtonElement>('input[type="submit"]')?.click();
            setIsExitModalOpen(isValid);
          }}
          onClose={onClose}
          isSubmitting={submitType === 'exitSaveAsDraft'}
        />
        <CreateInvoiceScreen
          isSaving={isSaving ? submitType : undefined}
          isLoading={isLoading}
          isLoadingSummary={isFetchingInvoiceSummary}
          onClose={handleClose}
          discount={{ id: discountId, onRemove: onRemoveDiscount, onEdit: onSetDiscount }}
          taxRate={{ id: taxRateId, onEdit: onSetTaxRate }}
          onSubmit={onSubmit}
          defaultValues={{
            invoiceNumber: displayInvoiceNumber,
            customerId: customers?.find((customer) => customer.id === customerId)?.id,
          }}
          error={error}
          serviceCharges={{
            onAdd: onAddServiceCharge,
            onRemove: onRemoveServiceCharge,
          }}
          invoiceSummary={invoiceSummary}
          onLineItemsChange={setLineItems}
          onFormStateChange={({ isDirty, isValid }) => {
            setIsDirty(isDirty);
            setIsValid(isValid);
          }}
          {...props}
          ref={ref}
        />
      </>
    );
  }
);
CreateInvoiceActivity.displayName = 'CreateInvoiceActivity';
